import {
  AloftTime,
  formatLongRevisionType,
  getResolvedAlertCommentaryItems,
} from '@aloft/toolkit-shared';
import { Text, View } from '@react-pdf/renderer';

import { getPreviousCommentariesForAddress } from '../../reconciliation/utils/getPreviousAlternativeComparablesEntries.ts';
import { CommentarySection } from '../components/CommentarySection.tsx';
import { List } from '../components/List.tsx';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { fontStyles, gridStyles, reconciliationPageStyles } from '../styles.ts';
import type { ReconciliationPageProps } from '../types.ts';

export const ReconciliationPage = ({
  subjectAddress,
  reconciliationCommentary,
  aivReconciliationCommentary,
  arvReconciliationCommentary,
  highestAndBestUseCommentary,
  feasibilityCommentary,
  alternativeComparablesCommentary,
  totalCostToCureCommentary,
  revisionCommentary,
  previousRevisionCommentaries,
  previousAlternativeComparablesCommentaries,
  previousRevisionsData,
  isRevision,
  revisionType,
  commentaryV2,
  alerts,
  revisionData,
  includeTotalCostToCure,
  hasArvOption,
  hasNegativeROI,
  arvBelowAivOverrideCommentary,
  aivDiscrepancyCommentary,
  arvDiscrepancyCommentary,
}: ReconciliationPageProps) => {
  const alertCommentaryItems = getResolvedAlertCommentaryItems(alerts, commentaryV2.alerts);
  const shouldDisplayAlertCommentarySection = alertCommentaryItems.length > 0;

  const previousRevisionRounds = (previousRevisionCommentaries ?? [])
    .map((commentary, index) => ({
      commentary,
      revisionType: previousRevisionsData?.[index]?.revisionType ?? 'revision',
      requestedAt: previousRevisionsData?.[index]?.requestedAt,
      altComps: previousAlternativeComparablesCommentaries?.[index]?.comparables ?? [],
    }))
    .filter(({ commentary, altComps }) => commentary.trim().length > 0 || altComps.length > 0)
    .toReversed();

  const resolvedAivCommentary = aivReconciliationCommentary ?? reconciliationCommentary;

  return (
    <Tkv2PDFPage title="Reconciliation" address={subjectAddress}>
      {includeTotalCostToCure && (
        <CommentarySection
          title="Total Cost to Cure Commentary"
          commentary={totalCostToCureCommentary}
        />
      )}

      {isRevision && (
        <>
          {previousRevisionRounds.map(
            ({ commentary, revisionType: prevType, requestedAt, altComps }, index) => (
              <View
                key={`previous-revision-round-${index}`}
                style={reconciliationPageStyles.section}
              >
                {commentary.trim().length > 0 && (
                  <>
                    <Text style={fontStyles.h5}>
                      Previous {formatLongRevisionType(prevType)} Commentary
                      {requestedAt
                        ? ` — ${AloftTime.schema.parse(requestedAt).formatTime(AloftTime.getCurrentTimeZone(), 'MM/dd/yy')}`
                        : ''}
                    </Text>
                    <Text style={fontStyles.body}>{commentary.replace('\n', '\n\n')}</Text>
                  </>
                )}
                {altComps.map((comp, altIndex) => (
                  <View
                    key={`previous-alt-comp-${index}-${altIndex}`}
                    style={reconciliationPageStyles.section}
                  >
                    <Text style={fontStyles.bodyBold}>
                      Previous {formatLongRevisionType(prevType)} Alternative Comparable:{' '}
                      {comp.address || 'N/A'}
                    </Text>
                    <Text style={fontStyles.body}>{comp.commentary.replace('\n', '\n\n')}</Text>
                  </View>
                ))}
              </View>
            ),
          )}

          <View style={reconciliationPageStyles.section}>
            <Text style={fontStyles.h5}>{revisionType} Commentary</Text>
            <Text style={fontStyles.body}>{revisionCommentary?.replace('\n', '\n\n')}</Text>
          </View>

          {(revisionData?.comparables ?? []).map((comp, index) => {
            const commentary = alternativeComparablesCommentary?.[index];
            const previousCommentaries = getPreviousCommentariesForAddress(
              previousAlternativeComparablesCommentaries,
              comp.address,
            );

            return (
              <View
                key={comp.address || `alternative-comparable-${index}`}
                style={reconciliationPageStyles.section}
              >
                <Text style={fontStyles.bodyBold}>
                  Alternative Comparable {index + 1}: {comp.address || 'N/A'}
                </Text>
                {previousCommentaries.map(
                  ({ commentary: previousCommentary, revisionType: prevType }, historyIndex) => (
                    <View
                      key={`previous-alternative-comparable-${comp.address}-${historyIndex}`}
                      style={reconciliationPageStyles.section}
                    >
                      <Text style={fontStyles.h5}>
                        Previous {formatLongRevisionType(prevType)} Commentary
                      </Text>
                      <Text style={fontStyles.body}>
                        {previousCommentary.replace('\n', '\n\n')}
                      </Text>
                    </View>
                  ),
                )}
                {commentary ? (
                  <Text style={fontStyles.body}>{commentary.replace('\n', '\n\n')}</Text>
                ) : null}
              </View>
            );
          })}
        </>
      )}

      {hasNegativeROI && feasibilityCommentary && (
        <View style={reconciliationPageStyles.section}>
          <Text style={fontStyles.h5}>Feasibility Commentary</Text>
          <Text style={fontStyles.body}>{feasibilityCommentary?.replace('\n', '\n\n')}</Text>
        </View>
      )}

      {arvBelowAivOverrideCommentary && (
        <View style={reconciliationPageStyles.section}>
          <Text style={fontStyles.h5}>Value Override Justification</Text>
          <Text style={fontStyles.body}>{arvBelowAivOverrideCommentary.replace('\n', '\n\n')}</Text>
        </View>
      )}

      {aivDiscrepancyCommentary && (
        <View style={reconciliationPageStyles.section}>
          <Text style={fontStyles.h5}>AIV Discrepancy Commentary</Text>
          <Text style={fontStyles.body}>{aivDiscrepancyCommentary.replace('\n', '\n\n')}</Text>
        </View>
      )}

      {arvDiscrepancyCommentary && (
        <View style={reconciliationPageStyles.section}>
          <Text style={fontStyles.h5}>ARV Discrepancy Commentary</Text>
          <Text style={fontStyles.body}>{arvDiscrepancyCommentary.replace('\n', '\n\n')}</Text>
        </View>
      )}

      {hasArvOption ? (
        <>
          <View style={reconciliationPageStyles.section}>
            <Text style={fontStyles.h5}>AIV Reconciliation Commentary</Text>
            <Text style={fontStyles.body}>{resolvedAivCommentary?.replace('\n', '\n\n')}</Text>
          </View>
          <View style={reconciliationPageStyles.section}>
            <Text style={fontStyles.h5}>ARV Reconciliation Commentary</Text>
            <Text style={fontStyles.body}>
              {arvReconciliationCommentary?.replace('\n', '\n\n')}
            </Text>
          </View>
        </>
      ) : (
        <View style={reconciliationPageStyles.section}>
          <Text style={fontStyles.h5}>Reconciliation Commentary</Text>
          <Text style={fontStyles.body}>{reconciliationCommentary?.replace('\n', '\n\n')}</Text>
        </View>
      )}
      <View style={reconciliationPageStyles.section}>
        <Text style={fontStyles.h5}>Highest & Best Use Commentary</Text>
        <Text style={fontStyles.body}>{highestAndBestUseCommentary?.replace('\n', '\n\n')}</Text>
      </View>
      {shouldDisplayAlertCommentarySection && (
        <View style={{ ...gridStyles.gridSection, marginRight: 20 }}>
          <Text style={fontStyles.h5}>Notes</Text>
          <List
            type="number"
            items={alertCommentaryItems.map(({ key, message, commentary }) => (
              <Text key={key} style={fontStyles.body}>
                <Text style={fontStyles.bodyBold}>{message}&nbsp;</Text>
                {commentary}
              </Text>
            ))}
          />
        </View>
      )}
    </Tkv2PDFPage>
  );
};
